import { useContext } from '@nuxtjs/composition-api';

export interface Question {
  id: number;
  title: string;
  singleSelection: boolean;
  options: Option[];
  paramName: string;
  dependencyParamName?: string;
  lastQuestion?: boolean;
}

export interface Option {
  value: string | number[];
  icon: string;
}

export function getQuestions() {
  const {
    app: { i18n },
  } = useContext();

  const motorSupportQuestion = {
    id: 1,
    title: i18n.t('I am looking for a bike') as string,
    singleSelection: true,
    options: [
      {
        value: 'e-bikes',
        icon: 'basic-questions/motor-with.svg',
      },
      {
        value: 'fahrrader',
        icon: 'basic-questions/motor-without.svg',
      },
    ],
    paramName: 'bikeType',
  };

  const categoryOptionsWithEngine = [
    {
      value: 'e-mountainbikes',
      icon: 'category-with-engine/e-mtb.svg',
    },
    {
      value: 'e-crossbikes',
      icon: 'category-with-engine/e-trekking-cross.svg',
    },
    {
      value: 'e-bikes-fur-kinder',
      icon: 'category-with-engine/e-kinder.svg',
    },
    {
      value: 'light-e-rennrader',
      icon: 'category-with-engine/e-rennrad.svg',
    },
    {
      value: 'light-e-gravelbikes',
      icon: 'category-with-engine/e-gravel.svg',
    },
  ];

  const categoryOptionsWithoutEngine = [
    {
      value: 'rennrader',
      icon: 'category-without-engine/rennrad.svg',
    },
    {
      value: 'gravelbikes',
      icon: 'category-without-engine/gravel.svg',
    },
    {
      value: 'mountainbikes',
      icon: 'category-without-engine/mountainbike.svg',
    },
    {
      value: 'crossbikes',
      icon: 'category-without-engine/cross-trekking.svg',
    },
    {
      value: 'kinderrader',
      icon: 'category-without-engine/kinderrad.svg',
    },
  ];

  function getCategoryQuestion(withEngine) {
    return {
      id: 2,
      title: i18n.t('I would like a') as string,
      singleSelection: true,
      options: withEngine ? categoryOptionsWithEngine : categoryOptionsWithoutEngine,
      paramName: 'category',
    };
  }

  const questionsByCategories: { [key: string]: Question[] } = {
    // without engine
    rennrader: [
      {
        id: 3,
        title: i18n.t('What requirements') as string,
        singleSelection: false,
        options: [
          {
            value: '88',
            icon: 'misc/berg.svg',
          },
          {
            value: '89',
            icon: 'misc/aero.svg',
          },
          {
            value: '90',
            icon: 'misc/komfort.svg',
          },
        ],
        paramName: 'area_of_use',
      },
      {
        id: 4,
        title: i18n.t('How much do you want to spend') as string,
        singleSelection: false,
        options: [
          {
            value: '0-2200',
            icon: 'price/preis-einsteiger-bis2200.svg',
          },
          {
            value: '2200-5000',
            icon: 'price/preis-preis_leistung-von2200-bis5000.svg',
          },
          {
            value: '5000-10000',
            icon: 'price/preis-premium-von5000-bis9999.svg',
          },
          {
            value: '10000-20000',
            icon: 'price/preis-topspec-ab9999.svg',
          },
        ],
        paramName: 'price',
        lastQuestion: true,
      },
    ],
    gravelbikes: [
      {
        id: 5,
        title: i18n.t('Gravel translation') as string,
        singleSelection: false,
        options: [
          {
            value: '101',
            icon: 'misc/uebersetzung-1fach.svg',
          },
          {
            value: '102',
            icon: 'misc/uebersetzung-2fach.svg',
          },
        ],
        paramName: 'transmission',
      },
      {
        id: 6,
        title: i18n.t('How much do you want to spend') as string,
        singleSelection: false,
        options: [
          {
            value: '0-1500',
            icon: 'price/preis-einsteiger-bis1500.svg',
          },
          {
            value: '1500-3400',
            icon: 'price/preis-preis_leistung-von1500-bis3400.svg',
          },
          {
            value: '4000-8000',
            icon: 'price/preis-premium-von3400-bis8000.svg',
          },
          {
            value: '8000-100000',
            icon: 'price/preis-topspec-ab8000.svg',
          },
        ],
        paramName: 'price',
        lastQuestion: true,
      },
    ],
    mountainbikes: [
      {
        id: 7,
        title: i18n.t('Hardtail or fully') as string,
        singleSelection: true,
        options: [
          {
            value: 'hardtail',
            icon: 'misc/mtb-hardtail.svg',
          },
          {
            value: 'fully',
            icon: 'misc/mtb-fully.svg',
          },
        ],
        paramName: 'hardtail-or-fully',
        dependencyParamName: 'hardtail-or-fully',
      },
    ],
    crossbikes: [
      {
        id: 7,
        title: i18n.t('Crossbike unterground') as string,
        singleSelection: true,
        options: [
          {
            value: 'on',
            icon: 'misc/untergrund-on-road.svg',
          },
          {
            value: 'off',
            icon: 'misc/untergrund-off-road.svg',
          },
        ],
        paramName: 'on-off-road',
        dependencyParamName: 'on-off-road',
      },
    ],
    kinderrader: [
      {
        id: 10,
        title: i18n.t('Kidsbike body size') as string,
        singleSelection: false,
        options: [
          {
            value: '110',
            icon: 'misc/koerpergroesse-90-110.svg',
          },
          {
            value: '111',
            icon: 'misc/koerpergroesse-110-120.svg',
          },
          {
            value: '112',
            icon: 'misc/koerpergroesse-120-130.svg',
          },
          {
            value: '113',
            icon: 'misc/koerpergroesse-130-150.svg',
          },
        ],
        paramName: 'body_height_kids',
      },
      {
        id: 11,
        title: i18n.t('How much do you want to spend') as string,
        singleSelection: false,
        options: [
          {
            value: '0-1000',
            icon: 'price/preis-preis_leistung-bis1000.svg',
          },
          {
            value: '1000-100000',
            icon: 'price/preis-premium-ab1000.svg',
          },
        ],
        paramName: 'price',
        lastQuestion: true,
      },
    ],
    // with engine
    'e-mountainbikes': [
      {
        id: 7,
        title: i18n.t('Hardtail or fully') as string,
        singleSelection: true,
        options: [
          {
            value: 'e-hardtail',
            icon: 'misc/mtb-hardtail.svg',
          },
          {
            value: 'e-fully',
            icon: 'misc/mtb-fully.svg',
          },
        ],
        paramName: 'hardtail-or-fully',
        dependencyParamName: 'hardtail-or-fully',
      },
    ],
    'e-bikes-fur-kinder': [
      {
        id: 12,
        title: i18n.t('Wheel inches') as string,
        singleSelection: false,
        options: [
          {
            value: '63',
            icon: 'misc/laufraddimension-24.svg',
          },
          {
            value: '64',
            icon: 'misc/laufraddimension-26.svg',
          },
        ],
        paramName: 'wheel_size',
        lastQuestion: true,
      },
    ],
    'light-e-rennrader': [
      {
        id: 13,
        title: i18n.t('How much do you want to spend') as string,
        singleSelection: false,
        options: [
          {
            value: '0-4000',
            icon: 'price/preis-preis_leistung-bis4000.svg',
          },
          {
            value: '4000-100000',
            icon: 'price/preis-premium-ab4000.svg',
          },
        ],
        paramName: 'price',
        lastQuestion: true,
      },
    ],
    'light-e-gravelbikes': [
      {
        id: 14,
        title: i18n.t('How much do you want to spend') as string,
        singleSelection: false,
        options: [
          {
            value: '0-4000',
            icon: 'price/preis-preis_leistung-bis4000.svg',
          },
          {
            value: '4000-100000',
            icon: 'price/preis-premium-ab4000.svg',
          },
        ],
        paramName: 'price',
        lastQuestion: true,
      },
    ],
    'e-crossbikes': [
      {
        id: 26,
        title: i18n.t('Crossbike unterground') as string,
        singleSelection: true,
        options: [
          {
            value: 'on',
            icon: 'misc/untergrund-on-road.svg',
          },
          {
            value: 'off',
            icon: 'misc/untergrund-off-road.svg',
          },
        ],
        paramName: 'on-off-road',
        dependencyParamName: 'on-off-road',
      },
      {
        id: 27,
        title: i18n.t('Frame shape') as string,
        singleSelection: false,
        options: [
          {
            value: '56',
            icon: 'misc/rahmen-diamant.svg',
          },
          {
            value: '57',
            icon: 'misc/rahmen-trapez.svg',
          },
          {
            value: '58',
            icon: 'misc/rahmen-tiefeinsteiger.svg',
          },
        ],
        paramName: 'frame_shape',
      },
    ],
  };

  const noEngineMtbHardTailQuestions = [
    {
      id: 15,
      title: i18n.t('Frame which material') as string,
      singleSelection: false,
      options: [
        {
          value: '59',
          icon: 'misc/material-aluminium.svg',
        },
        {
          value: '60',
          icon: 'misc/material-carbon.svg',
        },
      ],
      paramName: 'material',
    },
    {
      id: 16,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-1100',
          icon: 'price/preis-einsteiger-bis1100.svg',
        },
        {
          value: '1100-3500',
          icon: 'price/preis-preis_leistung-von1100-bis3500.svg',
        },
        {
          value: '3500-6400',
          icon: 'price/preis-premium-von3500-bis6400.svg',
        },
        {
          value: '6400-100000',
          icon: 'price/preis-topspec-ab6400.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const noEngineCrossOnRoadQuestions = [
    {
      id: 34,
      title: i18n.t('Suspension fork') as string,
      singleSelection: true,
      options: [
        {
          value: 'trekkingbikes',
          icon: 'misc/federgabel.svg',
        },
        {
          value: 'alltagsrader',
          icon: 'misc/starrgabel.svg',
        },
      ],
      paramName: 'onRoadSubcategory',
    },
    {
      id: 35,
      title: i18n.t('Frame shape') as string,
      singleSelection: false,
      options: [
        {
          value: '56',
          icon: 'misc/rahmen-diamant.svg',
        },
        {
          value: '57',
          icon: 'misc/rahmen-trapez.svg',
        },
        {
          value: '58',
          icon: 'misc/rahmen-tiefeinsteiger.svg',
        },
      ],
      paramName: 'frame_shape',
    },
    {
      id: 36,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-2000',
          icon: 'price/preis-preis_leistung-bis2000.svg',
        },
        {
          value: '2000-100000',
          icon: 'price/preis-premium-ab2000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const noEngineCrossOffRoadQuestions = [
    {
      id: 37,
      title: i18n.t('Frame shape') as string,
      singleSelection: false,
      options: [
        {
          value: '56',
          icon: 'misc/rahmen-diamant.svg',
        },
        {
          value: '57',
          icon: 'misc/rahmen-trapez.svg',
        },
        {
          value: '58',
          icon: 'misc/rahmen-tiefeinsteiger.svg',
        },
      ],
      paramName: 'frame_shape',
    },
    {
      id: 38,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-2000',
          icon: 'price/preis-preis_leistung-bis2000.svg',
        },
        {
          value: '2000-100000',
          icon: 'price/preis-premium-ab2000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const noEngineMtbFullyQuestions = [
    {
      id: 17,
      title: i18n.t('Where are you travelling') as string,
      singleSelection: false,
      options: [
        {
          value: '106',
          icon: 'misc/federweg-schotter-forst.svg',
        },
        {
          value: '107',
          icon: 'misc/federweg-wald-trail.svg',
        },
        {
          value: [108, 109],
          icon: 'misc/federweg-enduro-bikepark.svg',
        },
      ],
      paramName: 'fork_travel',
    },
    {
      id: 18,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-2400',
          icon: 'price/preis-einsteiger-bis2400.svg',
        },
        {
          value: '2400-5000',
          icon: 'price/preis-preis_leistung-von2400-bis5000.svg',
        },
        {
          value: '5000-9000',
          icon: 'price/preis-premium-von5000-bis9000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineMtbHardtailQuestions = [
    {
      id: 19,
      title: i18n.t('Frame shape') as string,
      singleSelection: false,
      options: [
        {
          value: '56',
          icon: 'misc/rahmen-diamant.svg',
        },
        {
          value: '57',
          icon: 'misc/rahmen-trapez.svg',
        },
        {
          value: '58',
          icon: 'misc/rahmen-tiefeinsteiger.svg',
        },
      ],
      paramName: 'frame_shape',
    },
    {
      id: 20,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-3000',
          icon: 'price/preis-einsteiger-bis3000.svg',
        },
        {
          value: '3000-4000',
          icon: 'price/preis-preis_leistung-von3000-bis4000.svg',
        },
        {
          value: '4000-100000',
          icon: 'price/preis-premium-ab4000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineMtbFullyQuestions = [
    {
      id: 21,
      title: i18n.t('Weight or range') as string,
      singleSelection: true,
      options: [
        {
          value: 'weight',
          icon: 'misc/gewicht.svg',
        },
        {
          value: 'range',
          icon: 'misc/reichweite.svg',
        },
      ],
      paramName: 'weight-or-range',
      dependencyParamName: 'weight-or-range',
    },
  ];

  const withEngineMtbFullyLightQuestions = [
    {
      id: 22,
      title: i18n.t('Where are you travelling') as string,
      singleSelection: false,
      options: [
        {
          value: '106',
          icon: 'misc/federweg-schotter-forst.svg',
        },
        {
          value: '107',
          icon: 'misc/federweg-wald-trail.svg',
        },
        {
          value: [108, 109],
          icon: 'misc/federweg-enduro-bikepark.svg',
        },
      ],
      paramName: 'fork_travel',
    },
    {
      id: 23,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-7000',
          icon: 'price/preis-premium-bis7000.svg',
        },
        {
          value: '7000-100000',
          icon: 'price/preis-topspec-ab7000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineMtbFullyNoLightQuestions = [
    {
      id: 24,
      title: i18n.t('Where are you travelling') as string,
      singleSelection: false,
      options: [
        {
          value: '106',
          icon: 'misc/federweg-schotter-forst.svg',
        },
        {
          value: '107',
          icon: 'misc/federweg-wald-trail.svg',
        },
        {
          value: [108, 109],
          icon: 'misc/federweg-enduro-bikepark.svg',
        },
      ],
      paramName: 'fork_travel',
    },
    {
      id: 28,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-4300',
          icon: 'price/preis-einsteiger-bis4300.svg',
        },
        {
          value: '4300-6500',
          icon: 'price/preis-preis_leistung-von4300-bis6500.svg',
        },
        {
          value: '6500-10000',
          icon: 'price/preis-premium-von6500-bis9999.svg',
        },
        {
          value: '10000-100000',
          icon: 'price/preis-topspec-ab9999.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineCrossOffRoadQuestions = [
    {
      id: 30,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-3000',
          icon: 'price/preis-preis_leistung-bis3000.svg',
        },
        {
          value: '3000-100000',
          icon: 'price/preis-premium-ab3000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineCrossOnRoadQuestions = [
    {
      id: 31,
      title: i18n.t('Weight or range') as string,
      singleSelection: true,
      options: [
        {
          value: 'weight',
          icon: 'misc/gewicht.svg',
        },
        {
          value: 'range',
          icon: 'misc/reichweite.svg',
        },
      ],
      paramName: 'weight-or-range',
      dependencyParamName: 'weight-or-range',
    },
  ];

  const withEngineCrossOffRoadETrekkingQuestions = [
    {
      id: 32,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-2300',
          icon: 'price/preis-preis_leistung-bis2300.svg',
        },
        {
          value: '2300-100000',
          icon: 'price/preis-premium-ab2300.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  const withEngineCrossOffRoadLightETrekkingQuestions = [
    {
      id: 33,
      title: i18n.t('How much do you want to spend') as string,
      singleSelection: false,
      options: [
        {
          value: '0-4000',
          icon: 'price/preis-preis_leistung-bis4000.svg',
        },
        {
          value: '4000-100000',
          icon: 'price/preis-premium-ab4000.svg',
        },
      ],
      paramName: 'price',
      lastQuestion: true,
    },
  ];

  function getDependencyQuestions(param: string, value: string, hasHardtailOrFullyOption: boolean, withEngine: boolean): Question[] {
    if (param === 'hardtail-or-fully' && value === 'hardtail') {
      return noEngineMtbHardTailQuestions;
    }
    if (param === 'hardtail-or-fully' && value === 'fully') {
      return noEngineMtbFullyQuestions;
    }
    if (param === 'hardtail-or-fully' && value === 'e-hardtail') {
      return withEngineMtbHardtailQuestions;
    }
    if (param === 'hardtail-or-fully' && value === 'e-fully') {
      return withEngineMtbFullyQuestions;
    }
    if (param === 'on-off-road' && value === 'on') {
      return withEngine ? withEngineCrossOnRoadQuestions : noEngineCrossOnRoadQuestions;
    }
    if (param === 'on-off-road' && value === 'off') {
      return withEngine ? withEngineCrossOffRoadQuestions : noEngineCrossOffRoadQuestions;
    }

    if (hasHardtailOrFullyOption) {
      if (param === 'weight-or-range' && value === 'weight') {
        return withEngineMtbFullyLightQuestions;
      }
      if (param === 'weight-or-range' && value === 'range') {
        return withEngineMtbFullyNoLightQuestions;
      }
    }

    if (param === 'weight-or-range' && value === 'weight') {
      return withEngineCrossOffRoadLightETrekkingQuestions;
    }

    if (param === 'weight-or-range' && value === 'range') {
      return withEngineCrossOffRoadETrekkingQuestions;
    }

    return [];
  }

  return {
    motorSupportQuestion,
    getCategoryQuestion,
    questionsByCategories,
    getDependencyQuestions,
  };
}
